import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupHeading } from 'utils/groupLabel';
import style from './EventParticipationTable.module.css';

const EventParticipationTable = ({ eventParticipationStats, season, pointsEligibleOnly, onMeetScopeChange }) => {
  const [tableData, setTableData] = useState([]);
  // A group's column is headed "U10 F", or "Y5 F" for a club that groups by year level, and the help text
  // names it the same way.
  const { groupingBasis, words } = useGroupLabels();
  const exampleGroup = formatGroupHeading(groupingBasis, 10);
  
  // Extract all unique age/gender combinations from the data
  const ageGenderColumns = useMemo(() => {
    if (!eventParticipationStats || eventParticipationStats.length === 0) {
      return [];
    }

    const uniqueCombinations = new Set();
    
    eventParticipationStats.forEach(event => {
      event.participationByAgeGender?.forEach(item => {
        uniqueCombinations.add(`${item.age}-${item.gender}`);
      });
    });

    // Convert to array and sort by age then gender
    return Array.from(uniqueCombinations)
      .map(combo => {
        const [age, gender] = combo.split('-');
        return { age: parseInt(age), gender };
      })
      .sort((a, b) => {
        if (a.age !== b.age) return a.age - b.age;
        return a.gender.localeCompare(b.gender);
      });
  }, [eventParticipationStats]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    setTableData(eventParticipationStats || []);
  }, [eventParticipationStats]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...tableData].sort((a, b) => {
      if (key === 'eventTypeName' || key === 'category' || key === 'classification') {
        // String comparison
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else if (key === 'meetsWithThisEvent') {
        // Numeric comparison
        return direction === 'asc'
          ? a[key] - b[key]
          : b[key] - a[key];
      }
      return 0;
    });

    setTableData(sortedData);
    setSortConfig({ key, direction });
  };

  const getMeetCount = (event, age, gender) => {
    if (!event.participationByAgeGender) return 0;
    
    const found = event.participationByAgeGender.find(
      item => item.age === age && item.gender === gender
    );
    
    return found ? found.meetCount : 0;
  };

  return (
    <div className={style.tableCard}>
      {/* Header with filter: which meets are counted */}
      <div className={style.tableHeader}>
        <div className={style.filterGroup}>
          <label htmlFor="meetScopeFilter" className={style.filterLabel}>
            Meets:
          </label>
          <select
            id="meetScopeFilter"
            className={style.selectInput}
            value={pointsEligibleOnly ? 'true' : 'false'}
            onChange={onMeetScopeChange}
          >
            <option value="true">Points-eligible meets only</option>
            <option value="false">All meets</option>
          </select>
        </div>
      </div>

      {/* What the numbers mean */}
      <p className={style.description}>
        Each row is an event type. <strong>Meets</strong> is the number of meets this season at which the
        event was run, counting a meet once it has at least one result in that event. Each {words.lower.value} and gender
        column shows at how many of those meets athletes in that group recorded a result in the event:
        a 4 under {exampleGroup} F on the 100m row means {exampleGroup} girls ran the 100m at four meets, and a dash means none.
        {pointsEligibleOnly
          ? ' Only meets that count for season points are counted.'
          : ' Every meet with results is counted, whether or not it counts for season points.'}
        {' '}Training sessions are never counted.
      </p>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              <th onClick={() => handleSort('eventTypeName')} className={style.stickyCol}>
                Event Type
              </th>
              <th onClick={() => handleSort('meetsWithThisEvent')}>
                Meets
              </th>
              {ageGenderColumns.map((col, index) => (
                <th key={index} className={style.ageGenderHeader}>
                  {formatGroupHeading(groupingBasis, col.age)} {col.gender}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td
                  colSpan={2 + ageGenderColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No event participation data available
                </td>
              </tr>
            ) : (
              tableData.map((event, index) => (
                <tr key={event.eventTypeCode || index}>
                  <td className={`${style.eventName} ${style.stickyCol}`}>
                    {event.eventTypeName}
                  </td>
                  <td className={style.meetsCount}>{event.meetsWithThisEvent}</td>
                  {ageGenderColumns.map((col, colIndex) => {
                    const count = getMeetCount(event, col.age, col.gender);
                    return (
                      <td 
                        key={colIndex} 
                        className={style.participationCell}
                      >
                        {count > 0 ? count : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

EventParticipationTable.propTypes = {
  eventParticipationStats: PropTypes.array,
  season: PropTypes.shape({
    seasonID: PropTypes.string,
    seasonName: PropTypes.string,
    status: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  pointsEligibleOnly: PropTypes.bool,
  onMeetScopeChange: PropTypes.func,
};

export default EventParticipationTable;
